// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { defineMessages, intlShape } from 'react-intl';
import { DEFAULT_COUNTRIES, getLocalizedCountry } from '../../../local-values/utils/countries';
import 'moment/locale/es';
import 'moment/locale/en-gb';
import classNames from 'classnames/bind';
import Filter from '../../../../elements/Filter/Filter';
import FlightBox from '../FlightBox/FlightBox';
import InfiniteScroll from '../../../../elements/InfiniteScroll/InfiniteScroll';
import Loading from '../../../../elements/Loading/Loading';
import FilterDrawer from '../../../../elements/Filter/Drawer/FilterDrawer';
import AlertContainer from '../../../alert/container/AlertContainer';

import commonStyle from '../FlightsViewCommon.scss';
import icons from '../../../../assets/styles/icons.scss';
import filterOptionsStyle from '../../../../assets/styles/filter-options.scss';
import filterStyles from '../../../../assets/styles/filter.scss';

import type { FilterCheckbox } from '../../../../elements/Filter/Filter';
import type { Flight } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { LocalValuesState } from '../../../local-values/duck';
import { generateTags } from '../../../../elements/Filter/utils';

type Props = {
  +fetchPastFlights: (queryFilter?: { [key: string]: string }, resetPagination?: boolean) => void;
  +resetFlightsState: () => void;
  +isFetchingPast?: boolean;
  +pastFlights?: Flight[];
  +page: number;
  +canAsk?: boolean;
  +fetchPastError?: HTTPError;
  +locale: string;
  +resetAlertState: () => void;
  +localValues: LocalValuesState;
};

type State = {
  drawerOpen: boolean;
  filter: {
    years: {
      [key: string]: boolean;
    };
    openYears: boolean;
    destination: {
      [key: string]: boolean;
    };
    openDestination: boolean;
  };
  order: {
    value: 'ASC' | 'DESC';
  };
};

const messages = defineMessages({
  title: {
    id: 'flights.past.document.title',
  },
  description: {
    id: 'flights.past.document.description',
  },
  sortPlaceholder: {
    id: 'sort.title',
  },
  sortMostRecent: {
    id: 'sort.option.mostRecent',
  },
  sortLatest: {
    id: 'sort.option.latest',
  },
});

const initialState = {
  drawerOpen: false,
  filter: {
    years: {},
    openYears: true,
    destination: {},
    openDestination: false,
  },
  order: {
    value: 'DESC',
  },
};

export default class FlightsPast extends Component<Props, State> {
  state = initialState;

  // Only in browser
  componentDidMount() {
    const { fetchPastFlights, pastFlights } = this.props;
    if (!pastFlights) {
      fetchPastFlights();
    }
  }

  componentWillUnmount() {
    const { resetFlightsState, resetAlertState } = this.props;
    resetAlertState();
    resetFlightsState();
  }

  handleOpenDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  handleCloseDrawer = () => {
    const { fetchPastFlights } = this.props;
    this.setState({
      drawerOpen: false,
    });
    fetchPastFlights(this.buildQueryParams(), true);
  };

  handleOpen(name: string) {
    this.setState((state: State) => {
      return {
        filter: update(state.filter, {
          [name]: { $set: !state.filter[name] }
        }),
      };
    });
  }

  handleChangeYear(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('years', { [value]: !filter.years[value] });
    }
  }

  handleChangeDestination(e: Event, isDesktop: boolean = true) {
    const { filter } = this.state;
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.handleChangeFilterValueCreator(isDesktop)('destination', { [value]: !filter.destination[value] });
    }
  }

  buildQueryParams(): { [key: string]: string } {
    const params = {};
    const { filter, order } = this.state;
    const years = Object.keys(filter.years)
      .reduce((acum, act: string) => filter.years[act] ? acum.concat(act) : acum, []);
    const destinations = Object.keys(filter.destination)
      .reduce((acum, act: string) => filter.destination[act] ? acum.concat(act) : acum, []);
    if (years.length) params.years = years.join(',');
    if (destinations.length) params.destinations = destinations.join(',');
    if (order.value) params.order = order.value;
    return params;
  }

  handleChangeSort = (e: Event) => {
    const { fetchPastFlights } = this.props;
    if (e.target instanceof HTMLSelectElement) {
      const option = e.target.options[e.target.selectedIndex];
      const dataset: { order: 'ASC' | 'DESC' } = (option.dataset: any);
      this.setState({
        order: {
          value: dataset.order,
        },
      }, () => fetchPastFlights(this.buildQueryParams(), true));
    }
  };

  handleClearFilters = () => {
    const { fetchPastFlights } = this.props;
    this.setState(initialState,
      () => fetchPastFlights(this.buildQueryParams(), true));
  };

  handleDeleteTag = (key?: string, name?: string) => {
    const { fetchPastFlights } = this.props;
    const updateQuery = {};
    switch (key) {
      case 'years':
      case 'destination':
        if (name) {
          updateQuery[key] = {
            [name]: { $set: false }
          };
        }
        break;
    }
    this.setState((prevState: State) => update(prevState, {
      filter: updateQuery,
    }), () => fetchPastFlights(this.buildQueryParams(), true));
  };

  // Depends if desktop or mobile
  handleChangeFilterValueCreator = (isDesktop: boolean) =>
    (name: string, value: any) => {
      const { fetchPastFlights } = this.props;
      this.setState((state: State) => {
        const c = update(state.filter[name], { $merge: value });
        return {
          filter: update(state.filter, {
            [name]: { $set: c }
          }),
        };
      }, () => {
        if (isDesktop) {
          fetchPastFlights(this.buildQueryParams(), true);
        }
      });
    };

  generateFilterFields = (years: ?FilterCheckbox[], destinations: ?FilterCheckbox[], { filter }: State, isDesktop: boolean) => [
    {
      type: 'checkbox',
      titleIntl: 'filter.years.title',
      handler: (e: Event) => this.handleChangeYear(e, isDesktop),
      stateValue: filter.years,
      name: 'years',
      checkboxes: years,
      color: 'orange',
      isOpen: filter.openYears,
      handleOpen: () => this.handleOpen('openYears'),
    },
    {
      type: 'checkbox',
      titleIntl: 'filter.destination.title',
      handler: (e: Event) => this.handleChangeDestination(e, isDesktop),
      stateValue: filter.destination,
      name: 'destination',
      checkboxes: destinations,
      color: 'orange',
      isOpen: filter.openDestination,
      handleOpen: () => this.handleOpen('openDestination'),
    },
  ];

  render() {
    const { pastFlights, isFetchingPast, fetchPastError, fetchPastFlights, canAsk, localValues } = this.props;
    const { drawerOpen } = this.state;
    const years = [
      {
        label: '2018',
        value: '2018',
      },
      {
        label: '2019',
        value: '2019',
      },
      {
        label: '2020',
        value: '2020',
      },
    ];
    const destinations = (localValues.countries || DEFAULT_COUNTRIES).map((country) => ({
      label: getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap),
      value: country.key,
    }));
    const filterFields = this.generateFilterFields(years, destinations, this.state, true);
    const filterContent = <Filter filterFields={(this.generateFilterFields(years, destinations, this.state, true): any)}
                                  handleDeleteTag={this.handleDeleteTag}/>;
    const tags: any = generateTags((filterFields: any), this.handleDeleteTag, this.context.intl.formatMessage);
    const sortNativeSelect = (
      <select onChange={this.handleChangeSort}>
        <FormattedMessage id={messages.sortMostRecent.id}>{
          txt => <option value="mostRecent"
                         data-order="DESC">{txt}</option>
        }</FormattedMessage>
        <FormattedMessage id={messages.sortLatest.id}>{
          txt => <option value="latest"
                         data-order="ASC">{txt}</option>
        }</FormattedMessage>
      </select>
    );

    const noResult = (!pastFlights || !pastFlights.length) && !isFetchingPast;

    const flightsContent = pastFlights && (
      pastFlights.map((flight: Flight, n) => (
        <FlightBox key={n}
                   flight={flight}
                   localValues={localValues}
                   isPast/>
      )));

    return [
      <Helmet key="helmet">
        <title>{this.context.intl.formatMessage(messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
      </Helmet>,
      <section key="filterOptions" className={filterOptionsStyle.filterOptions}>
        <section className={filterOptionsStyle.header}>
          <FormattedMessage id="stores.filter.title">{
            text => <p className={filterOptionsStyle.title}><i className={icons.symbolTweak}/>{text}</p>
          }</FormattedMessage>
          <section className={filterOptionsStyle.sortOptions}>
            <FormattedMessage id="sort.title">{
              txt => <label>{txt}</label>
            }</FormattedMessage>
            {sortNativeSelect}
            {/*<i className={classNames(filterOptionsStyle.down, icons.iconChevronDownMedium)}/>
            <i className={classNames(filterOptionsStyle.up, icons.iconChevronUpMedium)}/>*/}
          </section>
          <section className={filterOptionsStyle.filterDrawer}>
            <FilterDrawer drawerOpen={drawerOpen}
                          filterFields={(this.generateFilterFields(years, destinations,
                            this.state, false): any)}
                          color="orange"
                          handleCloseDrawer={this.handleCloseDrawer}
                          handleOpenDrawer={this.handleOpenDrawer}
                          handleClearFilters={this.handleClearFilters}/>
          </section>
        </section>
      </section>,
      <section key="mainWrap" className={classNames(commonStyle.mainWrap, commonStyle.nonPaddingTop)}>
        <aside className={commonStyle.filterWrap}>
          {filterContent}
        </aside>
        <section className={commonStyle.rightWrap}>
          <AlertContainer showId="flights"/>
          {!!tags.length && <ul className={filterStyles.tags}>
            {tags}
          </ul>}
          <header>
            {fetchPastError && Math.round(fetchPastError.code / 100) === 5 ?
              <FormattedMessage id="httpErrors.fetch.500">{
                txt => <p>{txt}</p>
              }</FormattedMessage> :
              <FormattedMessage id={noResult ? 'flights.past.noResults' : 'flights.past.title'}>{
                txt => <p>{txt}</p>
              }</FormattedMessage>}
            {/*<section className={style.sortOptions}>
              <FormattedMessage id="sort.title">{
                txt => <label>{txt}</label>
              }</FormattedMessage>
              {sortNativeSelect}
              <i className={classNames(style.down, icons.iconChevronDownMedium)}/>
              <i className={classNames(style.up, icons.iconChevronUpMedium)}/>
            </section>*/}
          </header>

          <InfiniteScroll offset={350}
                           disabled={isFetchingPast || !canAsk}
                           handleCheckpoint={() => fetchPastFlights(this.buildQueryParams())}>
            <section className={commonStyle.flightsWrap}>
              {flightsContent}
            </section>
          </InfiniteScroll>
          {isFetchingPast && <Loading className={commonStyle.loading}
                                      color="orange"/>}
        </section>
      </section>
    ];
  }
}

FlightsPast.propTypes = {
  fetchPastFlights: PropTypes.func.isRequired,
  resetFlightsState: PropTypes.func.isRequired,
  isFetchingPast: PropTypes.bool,
  pastFlights: PropTypes.array,
  page: PropTypes.number,
  canAsk: PropTypes.bool,
  fetchPastError: PropTypes.object,
  locale: PropTypes.string.isRequired,
  resetAlertState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
};

FlightsPast.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/flights/components/FlightPast/FlightsPast.jsx